import type { ReactNode } from 'react';
import styles from './SegmentedControl.module.css';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** Optional leading icon (e.g. Lucide) */
  icon?: ReactNode;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  dense?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  dense,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={[styles.root, dense ? styles.dense : '']
        .filter(Boolean)
        .join(' ')}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            className={[styles.option, active ? styles.active : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onChange(opt.value)}
          >
            {opt.icon ? (
              <span className={styles.icon} aria-hidden>
                {opt.icon}
              </span>
            ) : null}
            <span className={styles.label}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
